import React from "react";
import { Box } from "@mui/material";
import styled from "@emotion/styled";
import { NonProContainer } from "@components/NonProContainer";
import { ComparisonValue, SelectCompare } from "../BaseChartTools";
import { SecuritiesChartSelectedType, SecuritiesChartType } from "./types";
import { ChartControlItem, AssetTypeSelector } from "./AssetTypeSelector";

const FilterBarContainer = styled(Box)`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing(2)};

  ${({ theme }) => theme.breakpoints.down("md")} {
    margin-top: ${({ theme }) => theme.spacing(0)};
  }
`;

const FilterBarRowDesktop = styled(Box, {
  shouldForwardProp: (prop) => prop !== "noTypes",
})<{ noTypes: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: ${({ noTypes }) => (noTypes ? "flex-end" : "space-between")};
  gap: ${({ theme }) => theme.spacing(2)};
`;

const FilterBarColumnMobile = styled(Box)`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

interface Props {
  assetTypes: SecuritiesChartSelectedType[];
  chartControl: ChartControlItem[];
  selectedTypes: SecuritiesChartSelectedType[];
  onToggleType: (value: SecuritiesChartSelectedType) => void;
  chartColors: Record<string, string>;
  isXs: boolean;
  showComparisonSelector: boolean;
  comparedTicker?: ComparisonValue;
  onComparedTickerChange: (value?: ComparisonValue) => void;
  onChartTypeChange?: (value: SecuritiesChartType) => void;
}

export const SecuritiesChartFilterBar = ({
  assetTypes,
  chartControl,
  selectedTypes,
  onToggleType,
  chartColors,
  isXs,
  showComparisonSelector,
  comparedTicker,
  onComparedTickerChange,
  onChartTypeChange,
}: Props) => {
  const handleComparisonChange = (value?: ComparisonValue) => {
    onComparedTickerChange(value);
    if (value) onChartTypeChange?.("performance");
  };

  const renderComparisonSelector = () => {
    if (!showComparisonSelector) return null;

    return (
      <Box width={{ xs: "100%", md: 320 }}>
        <NonProContainer>
          <SelectCompare
            value={comparedTicker}
            onChange={handleComparisonChange}
          />
        </NonProContainer>
      </Box>
    );
  };

  if (!assetTypes.length && !showComparisonSelector) {
    return null;
  }

  return (
    <FilterBarContainer>
      {isXs ? (
        <FilterBarColumnMobile>
          <AssetTypeSelector
            chartControl={chartControl}
            selectedTypes={selectedTypes}
            onToggleType={onToggleType}
            chartColors={chartColors}
            hasMultipleAssetTypes={assetTypes.length > 1}
          />
          {renderComparisonSelector()}
        </FilterBarColumnMobile>
      ) : (
        <FilterBarRowDesktop noTypes={assetTypes.length === 0}>
          <AssetTypeSelector
            chartControl={chartControl}
            selectedTypes={selectedTypes}
            onToggleType={onToggleType}
            chartColors={chartColors}
            hasMultipleAssetTypes={assetTypes.length > 1}
          />
          {renderComparisonSelector()}
        </FilterBarRowDesktop>
      )}
    </FilterBarContainer>
  );
};
